import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import {
  resetPassword,
  resetResetPasswordState,
  selectResetPassword,
} from "../../redux/slices/authSlice";
import PageTitle from "../../components/PageTitle";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams(); // Get token from URL

  const resetPasswordState = useSelector(selectResetPassword);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  // Reset state on unmount
  useEffect(() => {
    return () => {
      dispatch(resetResetPasswordState());
    };
  }, [dispatch]);

  // Check if token exists
  useEffect(() => {
    if (!token) {
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  // Handle Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (newPassword.length < 6) {
      setLocalError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match");
      return;
    }

    await dispatch(
      resetPassword({
        resetToken: token,
        newPassword,
      }),
    );
  };

  // Redirect to login on success
  useEffect(() => {
    if (resetPasswordState.success) {
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  }, [resetPasswordState.success, navigate]);

  return (
    <>
      <PageTitle
        title="Reset Password – RestaurantWala"
        description="Create a new password for your RestaurantWala account and securely regain access to your restaurant management dashboard."
        keywords="RestaurantWala reset password, reset account password, restaurant management software login"
      />
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          py: 4,
        }}
      >
        {/* Back to Home Button */}
        <Box
          sx={{
            position: "absolute",
            top: 24,
            left: 24,
            zIndex: 10,
          }}
        >
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "50px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              border: "1px solid rgba(255,255,255,0.2)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Back to Home
          </Button>
        </Box>

        {/* Decorative Background Elements */}
        {[
          { size: 300, top: "-10%", left: "-5%", opacity: 0.08 },
          { size: 400, bottom: "-15%", right: "-8%", opacity: 0.08 },
          { size: 200, top: "30%", right: "10%", opacity: 0.06 },
        ].map((circle, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: circle.size,
              height: circle.size,
              borderRadius: "50%",
              bgcolor: "white",
              opacity: circle.opacity,
              top: circle.top,
              left: circle.left,
              bottom: circle.bottom,
              right: circle.right,
            }}
          />
        ))}

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            {/* Logo & Title */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: resetPasswordState.success
                    ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                    : "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                  boxShadow: resetPasswordState.success
                    ? "0 4px 20px rgba(34,197,94,0.3)"
                    : "var(--shadow-orange-hover)",
                }}
              >
                {resetPasswordState.success ? (
                  <CheckCircleIcon sx={{ fontSize: 44, color: "white" }} />
                ) : (
                  <LockIcon sx={{ fontSize: 44, color: "white" }} />
                )}
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1.5rem", sm: "1.9rem" },
                  background: resetPasswordState.success
                    ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                    : "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 0.5,
                }}
              >
                {resetPasswordState.success
                  ? "Password Reset!"
                  : "Reset Password"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--text-light)", fontSize: "0.95rem" }}
              >
                {resetPasswordState.success
                  ? "Your password has been reset successfully"
                  : "Enter your new password below"}
              </Typography>
            </Box>

            {/* Error Alert */}
            {(resetPasswordState.error || localError) && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "var(--primary)",
                  },
                }}
              >
                {resetPasswordState.error || localError}
              </Alert>
            )}

            {/* Success Alert */}
            {resetPasswordState.success && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#22c55e",
                  },
                }}
              >
                Password reset successful! Redirecting to login...
              </Alert>
            )}

            {/* Reset Password Form */}
            {!resetPasswordState.success && (
              <Box component="form" onSubmit={handleResetPassword}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={resetPasswordState.loading}
                  helperText="Minimum 6 characters"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "var(--primary)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--primary)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--primary)",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "var(--primary)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{
                            "&:hover": {
                              color: "var(--primary)",
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={resetPasswordState.loading}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "var(--primary)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--primary)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--primary)",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "var(--primary)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          size="small"
                          sx={{
                            "&:hover": {
                              color: "var(--primary)",
                            },
                          }}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={
                    resetPasswordState.loading ||
                    !newPassword ||
                    !confirmPassword ||
                    newPassword !== confirmPassword ||
                    newPassword.length < 6
                  }
                  endIcon={
                    resetPasswordState.loading ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                  sx={{
                    py: 1.5,
                    background:
                      "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: "var(--shadow-orange-hover)",
                    },
                    "&:disabled": {
                      background: "var(--border)",
                      color: "var(--text-light)",
                    },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                    boxShadow: "var(--shadow-orange)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {resetPasswordState.loading
                    ? "Resetting..."
                    : "Reset Password"}
                </Button>
              </Box>
            )}

            {/* Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 3,
              }}
            >
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
              <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
                OR
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
            </Box>

            {/* Back to Login */}
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
                Remember your password?{" "}
                <Link
                  to="/login"
                  style={{
                    color: "var(--primary)",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "var(--primary-dark)";
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "var(--primary)";
                    e.target.style.textDecoration = "none";
                  }}
                >
                  Back to Login
                </Link>
              </Typography>
            </Box>
          </Paper>

          
        </Container>
        {/* Footer Text */}
          <Typography
           variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11,
                  fontWeight: 500,
                  zIndex: 1,
                }}
          >
            © {new Date().getFullYear()} RestaurantWala • Your Digital Restaurant Partner
          </Typography>
      </Box>
    </>
  );
};

export default ResetPassword;
